import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import axios from "axios";
import { Badge, Card, Col, Row, Spinner } from "react-bootstrap";
import {
  FaBuilding,
  FaClipboardCheck,
  FaEnvelope,
  FaIdBadge,
  FaMapMarkerAlt,
  FaPhone,
  FaUserTie,
} from "react-icons/fa";
import { useParams } from "react-router-dom";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

type UserProfile = {
  username?: string | null;
  email?: string | null;
};

type ExpertAdhesion = {
  id_adhesion?: number;
  account_role?: string | null;
  company_type?: string | null;
  company_profile_type?: string | null;
  company_name?: string | null;
  contact_name?: string | null;
  phone?: string | null;
  email?: string | null;
  wilaya?: string | null;
  factory_address?: string | null;
  factory_location?: string | null;
  status?: string | null;
  products?: Array<{ product_name?: string | null }>;
  certifications?: Array<{ certification_name?: string | null }>;
  support_needs?: Array<{ support_need_text?: string | null }>;
};

const valueOrDash = (value?: string | number | null) => {
  const text = String(value ?? "").trim();
  return text || "-";
};

const normalizeLabel = (value?: string | null) =>
  valueOrDash(value).replace(/_/g, " ");

const InfoCard = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value?: string | number | null;
}) => (
  <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
    <Card.Body className="d-flex gap-3 align-items-start">
      <div
        className="d-inline-flex align-items-center justify-content-center"
        style={{
          width: 42,
          height: 42,
          borderRadius: 10,
          background: "#eef2ff",
          color: "#3730a3",
          flex: "0 0 auto",
        }}
      >
        {icon}
      </div>
      <div>
        <div className="text-muted small fw-semibold">{label}</div>
        <div className="fw-semibold text-dark">{valueOrDash(value)}</div>
      </div>
    </Card.Body>
  </Card>
);

export default function ExpertProfile() {
  const { id_user } = useParams();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [adhesion, setAdhesion] = useState<ExpertAdhesion | null>(null);
  const [loading, setLoading] = useState(true);

  const targetUserId = useMemo(() => {
    if (id_user) return id_user;

    const rawUser = localStorage.getItem("user");
    const parsedUser = rawUser ? JSON.parse(rawUser) : null;

    return parsedUser?.id_user || localStorage.getItem("userid");
  }, [id_user]);

  useEffect(() => {
    const loadExpertProfile = async () => {
      if (!targetUserId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [profileResponse, adhesionResponse] = await Promise.all([
          axios.get(`${backendUrl}/api/profile/${targetUserId}`),
          axios.get(`${backendUrl}/api/adherer/user/${targetUserId}`),
        ]);

        setUserProfile(profileResponse.data?.[0] || null);
        setAdhesion(adhesionResponse.data?.data || null);
      } catch (error) {
        console.error("Erreur chargement profil expert:", error);
      } finally {
        setLoading(false);
      }
    };

    loadExpertProfile();
  }, [targetUserId]);

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3 mb-0">Chargement du profil expert...</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (!targetUserId) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning">Utilisateur expert introuvable.</div>
      </div>
    );
  }

  const companyName =
    adhesion?.company_name || userProfile?.username || `Expert #${targetUserId}`;
  const expertType =
    adhesion?.company_profile_type || adhesion?.company_type || "expert";

  return (
    <div className="container-fluid py-4">
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
        <Card.Body className="p-4">
          <div className="d-flex flex-wrap justify-content-between gap-3 align-items-start">
            <div>
              <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                <Badge bg="dark">Expert</Badge>
                <Badge bg="secondary">{normalizeLabel(expertType)}</Badge>
                {adhesion?.status && <Badge bg="info">{adhesion.status}</Badge>}
              </div>
              <h2 className="h3 mb-1">{companyName}</h2>
              <p className="text-muted mb-0">
                Profil expert separe du parcours OEM et fournisseur.
              </p>
            </div>
            <div className="text-muted small">
              ID utilisateur: <strong>{targetUserId}</strong>
              {adhesion?.id_adhesion ? (
                <>
                  <br />
                  Adhesion AIDA: <strong>{adhesion.id_adhesion}</strong>
                </>
              ) : null}
            </div>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-4">
        <Col md={6} xl={3}>
          <InfoCard icon={<FaUserTie />} label="Responsable(s)" value={adhesion?.contact_name} />
        </Col>
        <Col md={6} xl={3}>
          <InfoCard icon={<FaEnvelope />} label="Email" value={adhesion?.email || userProfile?.email} />
        </Col>
        <Col md={6} xl={3}>
          <InfoCard icon={<FaPhone />} label="Telephone" value={adhesion?.phone} />
        </Col>
        <Col md={6} xl={3}>
          <InfoCard icon={<FaMapMarkerAlt />} label="Wilaya" value={adhesion?.wilaya} />
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={7}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
            <Card.Body className="p-4">
              <h5 className="mb-3 d-flex align-items-center gap-2">
                <FaBuilding /> Informations cabinet / bureau d'etude
              </h5>
              <Row className="g-3">
                <Col md={6}>
                  <div className="text-muted small fw-semibold">Nom de l'entreprise</div>
                  <div className="fw-semibold">{companyName}</div>
                </Col>
                <Col md={6}>
                  <div className="text-muted small fw-semibold">Type</div>
                  <div className="fw-semibold">{normalizeLabel(expertType)}</div>
                </Col>
                <Col md={12}>
                  <div className="text-muted small fw-semibold">Adresse</div>
                  <div className="fw-semibold">
                    {valueOrDash(adhesion?.factory_address || adhesion?.factory_location)}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
            <Card.Body className="p-4">
              <h5 className="mb-3 d-flex align-items-center gap-2">
                <FaClipboardCheck /> Elements declares
              </h5>
              <div className="mb-3">
                <div className="text-muted small fw-semibold mb-2">Produits / domaines</div>
                {(adhesion?.products || []).length ? (
                  <ul className="mb-0 ps-3">
                    {adhesion?.products?.map((item, index) => (
                      <li key={`${item.product_name}-${index}`}>{valueOrDash(item.product_name)}</li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </div>
              <div>
                <div className="text-muted small fw-semibold mb-2">Certifications</div>
                {(adhesion?.certifications || []).length ? (
                  <div className="d-flex flex-wrap gap-2">
                    {adhesion?.certifications?.map((item, index) => (
                      <Badge bg="light" text="dark" key={`${item.certification_name}-${index}`}>
                        {valueOrDash(item.certification_name)}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
